<div>
<Navbar />
<Sidebar />

<div class="page-wrapper">
    <div class="hero-section w-100">
        <div class="border border-secondary rounded-4 p-2">

            <!-- Header -->
            <div class="mb-4">
                <h2 class="gradient-text fw-bold">User Permissions: {{ user_name }}</h2>
                <p class="text-muted">Manage permissions for this user</p>
            </div>

            <!-- User Info Card -->
            <div class="card mb-4 shadow-sm">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="card-title mb-0">User Information</h5>
                        <button class="btn btn-sm upload-button text-white"
                                @click="open_edit_profile_modal">
                            <i class="fas fa-edit me-1"></i> Edit Profile
                        </button>
                    </div>
                    <div class="row">
                        <div class="col-md-3">
                            <small class="text-muted d-block">Email</small>
                            <strong>{{ user_email }}</strong>
                        </div>
                        <div class="col-md-3">
                            <small class="text-muted d-block">Role</small>
                            <strong>{{ user_role }}</strong>
                        </div>
                        <div class="col-md-3">
                            <small class="text-muted d-block">Client Type</small>
                            <strong>{{ user_client_type }}</strong>
                        </div>
                        <div class="col-md-3">
                            <small class="text-muted d-block">Status</small>
                            <strong>{{ user_status }}</strong>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabs -->
            <ul class="nav nav-tabs nav-justified mb-4">
                <li class="nav-item" role="presentation">
                    <button class="nav-link"
                        :class="{ active: active_tab === 'role' }"
                        @click="switch_tab('role')">
                        Role Permissions
                    </button>
                </li>
                <li class="nav-item">
                    <button class="nav-link"
                        :class="{ active: active_tab === 'user' }"
                        @click="switch_tab('user')">
                        User-Specific Permissions
                    </button>
                </li>
                <li class="nav-item">
                    <button class="nav-link"
                        :class="{ active: active_tab === 'all' }"
                        @click="switch_tab('all')">
                        Access Control
                    </button>
                </li>
            </ul>

            <AccessControlView
                v-if="active_tab === 'all'"
                :user_id="user_id"
                @back="go_back"
            />

            <div v-else>
                <!-- Stats Cards -->
                <div class="row text-center mb-3">
                    <div class="col-md-4 mb-3">
                        <div class="card rounded-4 shadow-sm users-card">
                            <div class="card-body">
                                <p class="card-text display-5">{{ total_count }}</p>
                                <h5 class="card-title">Total Assigned</h5>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-4 mb-3">
                        <div class="card rounded-4 shadow-sm users-card">
                            <div class="card-body">
                                <p class="card-text display-5">{{ active_count }}</p>
                                <h5 class="card-title">Active</h5>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-4 mb-3">
                        <div class="card rounded-4 shadow-sm users-card">
                            <div class="card-body">
                                <p class="card-text display-5">{{ inactive_count }}</p>
                                <h5 class="card-title">Inactive</h5>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="d-flex mb-3">
                    <button v-if="active_tab === 'user' || active_tab === 'all'"
                            class="upload-button btn-sm text-white box-shadow small d-flex align-items-center"
                            @click="open_assign_modal">
                        <i class="fas fa-plus plus-icon me-1"></i> Assign Permissions
                    </button>
                    <button class="upload-button btn-sm text-white box-shadow small d-flex align-items-center ms-2"
                            @click="go_back">
                        Back
                    </button>
                </div>

                <!-- Permissions Table -->
                <div class="card table-card">
                    <div class="card-body">
                        <div v-if="loading" class="text-center py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>

                        <div v-else class="table-responsive content-table">
                            <table id="permissions_table" class="table text-xs table-striped">
                                <thead>
                                    <tr>
                                        <th>No</th>
                                        <th>Permission Name</th>
                                        <th>Description</th>
                                        <th>Main Features</th>
                                        <th>Source</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody class="text-start text-xs">
                                    <tr v-for="(permission, index) in current_permissions" :key="permission.id || index">
                                        <td>{{ index + 1 }}</td>
                                        <td class="align-middle">
                                            <div class="d-flex align-items-center justify-content-between">
                                                <span>{{ permission.name }}</span>
                                                <span class="ms-2 cursor-pointer"
                                                    @click="show_permission_details(permission)"
                                                    title="View Details">
                                                    <i class="fas fa-info-circle"></i>
                                                </span>
                                            </div>
                                        </td>
                                        <td>{{ permission.description || 'No description' }}</td>
                                        <td>
                                            <div v-if="permission.main_features && permission.main_features.length > 0" class="d-flex flex-wrap gap-1">
                                                <span v-for="(feature, idx) in permission.main_features.slice(0, 2)"
                                                    :key="idx"
                                                    class="badge bg-light text-dark border"
                                                    :title="feature">
                                                    {{ feature }}
                                                </span>
                                                <button v-if="permission.main_features.length > 2"
                                                        class="badge bg-secondary border-0 cursor-pointer"
                                                        @click="show_permission_details(permission)">
                                                    +{{ permission.main_features.length - 2 }}
                                                </button>
                                            </div>
                                            <span v-else class="text-muted small">None</span>
                                        </td>
                                        <td>
                                            <span v-if="permission.source === 'role'">Role</span>
                                            <span v-else>User</span>
                                        </td>
                                        <td>
                                            <span v-if="permission.is_active"
                                                    class="badge bg-success">Active</span>
                                            <span v-else
                                                    class="badge bg-danger">Inactive</span>
                                        </td>

                                        <td>
                                            <div class="d-flex gap-2">
                                                <button v-if="permission.source === 'user'"
                                                        class="btn btn-sm btn-danger text-nowrap"
                                                        @click="remove_permission(permission)">
                                                    Remove
                                                </button>

                                                <button v-if="permission.source === 'role'"
                                                        class="btn btn-sm btn-secondary text-nowrap"
                                                        disabled
                                                        title="Cannot modify role permissions">
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</div>

<!-- Permission Details Modal -->
<div v-if="show_details_modal" class="modal fade show d-block custom-backdrop" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable position-relative" >
        <div class="modal-content">
            <div class="modal-header">
                <div>
                    <h5 class="modal-title gradient-text">Details for: {{ selected_permission_details?.name }}</h5>
                </div>
                <button type="button" class="btn-close" @click="close_details_modal"></button>
            </div>

            <div class="modal-body">
                <div class="mb-4">
                    <h6 class="text-muted mb-3">
                        <i class="fas fa-cubes me-2"></i>Main Features & Access
                    </h6>
                    <div v-if="selected_permission_details?.main_features && selected_permission_details.main_features.length > 0"
                        class="row g-3">
                        <div v-for="(main_feature_obj, idx) in selected_permission_details.main_features_detailed"
                            :key="idx"
                            class="col-12">
                            <div class="card shadow-sm">
                                <div class="card-header bg-light">
                                    <h6 class="mb-0">
                                        {{ main_feature_obj.name }}
                                    </h6>
                                </div>
                                <div class="card-body">
                                    <div v-if="main_feature_obj.sub_features && main_feature_obj.sub_features.length > 0">
                                        <div class="table-responsive">
                                            <table class="table table-sm table-borderless mb-0">
                                                <tbody>
                                                    <tr v-for="(sub_feature, sf_idx) in main_feature_obj.sub_features"
                                                        :key="sf_idx">
                                                        <td>
                                                            <span class="small">{{ sub_feature.name }}</span>
                                                        </td>
                                                        <td>
                                                            <span v-if="sub_feature.crud_actions && sub_feature.crud_actions.length > 0">
                                                                <span v-for="(crud, c_idx) in sub_feature.crud_actions"
                                                                    :key="c_idx"
                                                                    class="badge bg-light text-dark border me-1 small">
                                                                    {{ crud }}
                                                                </span>
                                                            </span>
                                                            <span v-else class="text-muted small">No actions</span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <p v-else class="text-muted small mb-0">No sub-features configured</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="alert alert-info">
                        <i class="fas fa-info-circle me-2"></i>
                        No main features assigned to this permission yet.
                    </div>
                </div>
            </div>

            <div class="modal-footer">
            </div>
        </div>
    </div>
</div>

<!-- Edit Profile Modal -->
<div v-if="show_edit_profile_modal" class="modal fade show d-block custom-backdrop" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
        <div class="modal-header">
        <h5 class="modal-title gradient-text">Edit User Profile</h5>
        <button type="button"
                class="btn-close"
                @click="close_edit_profile_modal"
                :disabled="modal_loading"></button>
        </div>

        <div class="modal-body">
        <form @submit.prevent="save_profile_changes">
            <!-- First Name -->
            <div class="mb-3 text-start">
            <label for="first_name" class="ms-2">First Name <span class="text-danger">*</span></label>
            <input type="text"
                    class="form-control"
                    :class="{ 'is-invalid': edit_form_errors.first_name }"
                    id="first_name"
                    v-model="first_name"
                    placeholder="Enter first name"
                    :disabled="modal_loading">
            <div v-if="edit_form_errors.first_name" class="invalid-feedback">
                {{ edit_form_errors.first_name }}
            </div>
            </div>

            <!-- Last Name -->
            <div class="mb-3 text-start">
            <label for="last_name" class="ms-2">
                Last Name <span class="text-danger">*</span>
            </label>
            <input type="text"
                    class="form-control"
                    :class="{ 'is-invalid': edit_form_errors.last_name }"
                    id="last_name"
                    v-model="last_name"
                    placeholder="Enter last name"
                    :disabled="modal_loading">
            <div v-if="edit_form_errors.last_name" class="invalid-feedback">
                {{ edit_form_errors.last_name }}
            </div>
            </div>

            <!-- Email -->
            <div class="mb-3 text-start">
            <label for="email" class="ms-2">
                Email <span class="text-danger">*</span>
            </label>
            <input type="email"
                    class="form-control"
                    :class="{ 'is-invalid': edit_form_errors.email }"
                    id="email"
                    v-model="email"
                    placeholder="Enter email address"
                    :disabled="modal_loading">
            <div v-if="edit_form_errors.email" class="invalid-feedback">
                {{ edit_form_errors.email }}
            </div>
            </div>

            <!-- Role Select - FIXED: Now binds to role.id instead of role.name -->
            <div class="mb-3 text-start">
                <label for="edit_role_id" class="ms-2">Role <span class="text-danger">*</span></label>
                <select
                    class="form-select"
                    id="edit_role_id"
                    v-model="role_id"
                    :disabled="modal_loading"
                    :class="{ 'is-invalid': edit_form_errors.role }">
                    <option value="" disabled>Select a role</option>
                    <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.name }}</option>
                </select>
                <div v-if="edit_form_errors.role" class="invalid-feedback">
                    {{ edit_form_errors.role }}
                </div>
            </div>

            <!-- Client Type Select - FIXED: Now binds to type.id instead of type.name -->
            <div class="mb-3 text-start">
                <label for="client_type_id" class="ms-2">Client Type <span class="text-danger">*</span></label>
                <select
                    class="form-select"
                    id="client_type_id"
                    v-model="client_type_id"
                    :disabled="modal_loading"
                    :class="{ 'is-invalid': edit_form_errors.client_type }">
                    <option value="" disabled>Select a client type</option>
                    <option v-for="type in client_types" :key="type.id" :value="type.id">{{ type.name }}</option>
                </select>
                <div v-if="edit_form_errors.client_type" class="invalid-feedback">
                    {{ edit_form_errors.client_type }}
                </div>
            </div>

        </form>
        </div>

        <div class="modal-footer">
            <button type="button"
                    class="btn upload-button text-white"
                    @click="save_profile_changes"
                    :disabled="modal_loading">
                <span v-if="modal_loading"
                    class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="fas fa-save me-1"></i>
                {{ modal_loading ? 'Saving...' : 'Save Changes' }}
            </button>
        </div>
    </div>
    </div>
</div>

<!-- Enhanced Assign Permissions Modal -->
<div v-if="show_assign_modal" class="modal fade show d-block custom-backdrop" tabindex="-1">

    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable position-relative">
        <div class="modal-content">
            <div class="modal-header">
                <div>
                    <h5 class="modal-title gradient-text">Assign User Permissions to: {{ user_name }}</h5>
                </div>
                <button type="button" class="btn-close" @click="close_assign_modal"></button>
            </div>

            <div class="modal-body">
                <!-- Search Box -->
                <div class="mb-3">
                    <input type="text"
                           class="form-control"
                           placeholder="Search permissions..."
                           v-model="search_query">
                </div>

                <!-- Available Permissions List -->
                <div v-if="filtered_available_permissions.length > 0" class="permissions-list">
                    <div v-for="permission in filtered_available_permissions"
                        :key="permission.id"
                        class="card mb-2 permission-card"
                        :class="{
                            'border-primary': is_permission_selected(permission),
                            'bg-light': permission.is_assigned_to_user || permission.is_assigned_to_role
                        }">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between align-items-start">
                                <div class="flex-grow-1 me-3">
                                    <!-- Permission Header -->
                                    <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                        <h6 class="mb-0">{{ permission.name }}</h6>

                                        <span v-if="permission.main_features && permission.main_features.length > 0"
                                                class="p-0 mt-1 text-decoration-none"
                                                @click="show_permission_details(permission)"
                                                title="View full details">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                        <span v-if="permission.is_assigned_to_role" class="badge bg-success">
                                            Assigned via Role
                                        </span>
                                        <span v-else-if="permission.is_assigned_to_user" class="badge bg-success">
                                            Already Assigned
                                        </span>
                                    </div>

                                    <!-- Description -->
                                    <p class="text-muted small description-truncate"
                                       v-if="permission.description"
                                       :title="permission.description">
                                        {{ truncate_text(permission.description, 100) }}
                                    </p>

                                    <!-- Main Features -->
                                    <div v-if="permission.main_features && permission.main_features.length > 0">
                                        <div class="d-flex flex-wrap gap-1 mb-2">
                                            <span class="text-muted small me-1">Includes:</span>
                                            <span v-for="(feature, idx) in permission.main_features.slice(0, 3)"
                                                :key="idx"
                                                class="badge bg-light text-dark border small">
                                                {{ feature }}
                                            </span>
                                            <button v-if="permission.main_features.length > 3"
                                                    class="badge bg-secondary border-0 small cursor-pointer"
                                                    @click="show_permission_details(permission)">
                                                +{{ permission.main_features.length - 3 }} more
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Checkbox -->
                                <div class="form-check form-switch mt-1">
                                    <input class="form-check-input fs-4"
                                        type="checkbox"
                                        :checked="is_permission_selected(permission) || permission.is_assigned_to_role || permission.is_assigned_to_user"
                                        :disabled="permission.is_assigned_to_user || permission.is_assigned_to_role"
                                        @change="toggle_permission_selection(permission)">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="text-center py-5 text-muted">
                    <i class="fas fa-search display-4 mb-3 opacity-25"></i>
                    <p class="mb-1">No permissions found</p>
                    <small>Try adjusting your search criteria</small>
                </div>

                <!-- Selection Summary -->
                <div v-if="selected_permissions.length > 0"
                    class="alert alert-info mt-3 d-flex align-items-center justify-content-between">
                    <div>
                        <i class="fas fa-check-circle me-2"></i>
                        <strong>{{ selected_permissions.length }}</strong> permission(s) selected
                    </div>
                    <button class="btn btn-sm btn-light" @click="selected_permissions = []">
                        Clear
                    </button>
                </div>
            </div>

            <div class="modal-footer">
                <button type="button" class="btn upload-button text-white"
                        @click="assign_permissions"
                        :disabled="modal_loading || selected_permissions.length === 0">
                    <span v-if="modal_loading"
                        class="spinner-border spinner-border-sm me-2"></span>
                    {{ modal_loading ? 'Assigning...' : `Assign (${selected_permissions.length})` }}
                </button>
            </div>
        </div>
    </div>
</div>
</div>
